import { Gauge, TrendingUp, ShieldAlert } from "lucide-react";
import { RadialScore } from "./primitives/RadialScore";
import { ScoreBenchmarkBar } from "./primitives/ScoreBenchmarkBar";
import { PillarRadar } from "./primitives/PillarRadar";
import { GroupedBarChart, youVsOthersBarItems } from "./primitives/GroupedBarChart";
import { FindingsList } from "./primitives/FindingsList";
import PassedChecks from "./PassedChecks";
import GbpSelfCheck from "./GbpSelfCheck";
import { ComparisonBar } from "./primitives/ComparisonBar";
import { benchmarkPillars as benchmarkPillarsOf } from "./summaryModel";
import { PILLAR_MAX, VISIBILITY_PILLARS, scoreBand, formatMeasuredOn } from "./reportUtils";

export default function OverviewTab({ data, lang, L, tone, setActiveTab }) {
  const pillars = data.score_pillars || {};
  const findings = data.audit_findings || [];
  const top3 = data.top3 || [];
  const isDetailed = !!data.is_detailed;
  const legalIssues = data.legal?.issues || [];

  // server.py sends `measured_at` as an ISO timestamp; render it in the
  // recipient's own locale, date only — the time of day is noise here.
  const measuredOnLabel = formatMeasuredOn(data.measured_at, lang);
  const target = top3.length ? Math.min(...top3.map((t) => t.score)) : null;
  const gap = top3.length ? Math.max(top3[0].score - data.digital_score, 0) : 0;
  const pillarKeys = Object.keys(L.pillars);

  // Average of top3's per-pillar scores, for the radar's dashed benchmark
  // outline — only built when at least one competitor carries pillar data.
  const benchmarkPillars = benchmarkPillarsOf(top3, pillarKeys);

  return (
    <>
      <div className="rounded-2xl border bg-card p-6 shadow-sm sm:p-8">
        <div className="flex flex-col items-center gap-5 sm:flex-row">
          <RadialScore value={data.digital_score} max={100} size={104} strokeWidth={11} color={tone.bar}>
            <span className="text-2xl font-bold tabular-nums" style={{ color: tone.fg }}>
              {data.digital_score}
            </span>
          </RadialScore>
          <div className="text-center sm:text-left">
            <div className="flex items-center justify-center gap-1.5 text-base font-medium text-muted-foreground sm:justify-start">
              <Gauge className="h-4 w-4" /> {L.scoreLabel}
            </div>
            <div className="mt-0.5 text-base font-semibold" style={{ color: tone.fg }}>
              {scoreBand(data.digital_score, lang).emoji} {scoreBand(data.digital_score, lang).label}
            </div>
            {data.benchmark?.score_median != null && (
              <>
                <p className="mt-1 text-base">{L.benchmarkLine(data.benchmark.score_median)}</p>
                <ScoreBenchmarkBar
                  value={data.digital_score}
                  max={100}
                  median={data.benchmark.score_median}
                  color={tone.bar}
                  caption={L.benchmarkCaption}
                />
              </>
            )}
            {/* A measurement without a date is a claim, not a measurement. */}
            {measuredOnLabel && (
              <p className="mt-1 text-sm text-muted-foreground">
                {L.measuredOn(measuredOnLabel)} {L.verifyPromise}
              </p>
            )}
          </div>
        </div>

        {/* Radar gives the "shape" of strengths/weaknesses at a glance; the
            bars below it keep the exact per-pillar numbers — spec §5. */}
        <div className="mt-6">
          <PillarRadar
            pillars={pillars}
            pillarMax={PILLAR_MAX}
            pillarKeys={pillarKeys}
            pillarLabels={L.pillars}
            benchmarkPillars={benchmarkPillars}
          />
        </div>

        <div className="mt-6 space-y-2.5">
          {Object.entries(L.pillars).map(([key, label]) => {
            const val = pillars[key] ?? 0;
            const max = PILLAR_MAX[key];
            // Only the gbp pillar carries a manual-review coverage count (see
            // audit/scoring.py gbp_manual_progress) — an honest "N of 24
            // points manually reviewed" note, hidden once fully reviewed.
            const mp = key === "gbp" ? data.gbp_manual_progress : null;
            const showManualNote = mp && mp.reviewed < mp.total;
            return (
              <div key={key}>
                <div className="flex items-center gap-3">
                  <span className="w-40 shrink-0 text-sm text-muted-foreground">{label}</span>
                  <div className="h-2 flex-1 overflow-hidden rounded-full bg-secondary">
                    <div className="h-full rounded-full transition-all" style={{ width: `${(val / max) * 100}%`, background: tone.bar }} />
                  </div>
                  <span className="w-10 shrink-0 text-right text-sm tabular-nums text-muted-foreground">
                    {val}/{max}
                  </span>
                </div>
                {showManualNote && (
                  <p className="ml-[calc(10rem+0.75rem)] mt-1 text-xs text-muted-foreground" data-testid="gbp-manual-progress">
                    {L.manualReviewed(mp.reviewed, mp.total)}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {top3.length > 0 && (
        <div className="mt-6 overflow-hidden rounded-2xl border bg-card shadow-sm">
          <div className="border-b bg-secondary/50 p-5 sm:p-6">
            <h2 className="mb-1.5 flex items-center gap-1.5 font-heading text-lg font-semibold">
              <TrendingUp className="h-4 w-4" /> {L.comparisonTitle}
            </h2>
            <p className="text-base text-muted-foreground">{L.comparisonDesc}</p>
          </div>
          <div className="p-5 sm:p-6">
            {gap > 0 && (
              <div className="mb-5 flex items-center justify-center gap-4 text-center sm:gap-8">
                <div>
                  <div className="text-3xl font-bold tabular-nums" style={{ color: tone.fg }}>{data.digital_score}</div>
                  <div className="text-sm text-muted-foreground">{L.yourValue}</div>
                </div>
                <div className="flex flex-col items-center">
                  <span className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">{L.gapLabel}</span>
                  <span className="text-2xl font-bold text-amber-600">−{gap}</span>
                </div>
                <div>
                  <div className="text-3xl font-bold tabular-nums text-emerald-600">{top3[0].score}</div>
                  <div className="text-sm text-muted-foreground">{top3[0].label}</div>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <ComparisonBar label={L.you} value={data.digital_score} highlight tone={tone} />
              {top3.map((t, i) => (
                <ComparisonBar key={i} label={t.label} value={t.score} />
              ))}
            </div>

            {target != null &&
              (data.digital_score < target ? (
                <p className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-base text-amber-900">{L.targetBelow(target)}</p>
              ) : (
                <p className="mt-4 rounded-lg border border-green-200 bg-green-50 p-3 text-base text-green-900">{L.targetMet}</p>
              ))}

            {/* Small-multiples: one you-vs-top3 bar group per pillar, replacing
                the old cramped table whose competitor-name columns truncated
                at 70px on mobile (spec §5). */}
            {top3.some((t) => t.pillars) && (
              <div className="mt-5 space-y-4 rounded-lg border p-4">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">{L.pillarTableArea}</h3>
                {pillarKeys.map((key) => (
                  <div key={key}>
                    <p className="mb-1.5 text-sm font-medium text-muted-foreground">{L.pillars[key]}</p>
                    <GroupedBarChart
                      items={youVsOthersBarItems(
                        { label: L.you, value: pillars[key] ?? 0 },
                        top3.filter((t) => t.pillars).map((t) => ({ label: t.label, value: t.pillars[key] ?? 0 }))
                      )}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {((isDetailed && legalIssues.length > 0) || (!isDetailed && data.legal_teaser && data.legal_teaser.issue_count > 0)) && (
        <button
          type="button"
          onClick={() => setActiveTab("legal")}
          className="mt-6 flex w-full items-center gap-3 rounded-2xl border border-amber-300 bg-amber-50 p-4 text-left shadow-sm transition-colors hover:bg-amber-100"
        >
          <ShieldAlert className="h-5 w-5 shrink-0 text-amber-700" />
          <span className="flex-1 text-base text-amber-900">
            {L.legalAlert(
              isDetailed ? legalIssues.length : data.legal_teaser.issue_count,
              (isDetailed && legalIssues.some((f) => f.code === "stock_images_detected")) ||
                (!isDetailed && data.legal_teaser.has_image_license_flag)
            )}
          </span>
          <span className="shrink-0 text-sm font-semibold text-amber-800 underline">{L.details}</span>
        </button>
      )}

      {/* Detailed per-finding breakdown behind the score above — moved here
          from the Sichtbarkeit tab (user request): it covers all 5 of that
          radar's pillars (web/onpage/local/gbp/ai), not just AI-visibility
          signals, so it reads as the natural "see everything behind this
          score" continuation of the overview rather than Sichtbarkeit
          content. Reviews' own findings stay in ReputationTab. */}
      {isDetailed && <PassedChecks data={data} L={L} />}

      <FindingsList
        findings={findings}
        pillars={pillars}
        pillarMax={PILLAR_MAX}
        pillarKeys={VISIBILITY_PILLARS}
        pillarLabels={L.pillars}
        severityLabels={L.severity}
        headingText={L.foundHeading}
      />

      {/* Moved here from right under the score card (owner request
          2026-10-01): belongs next to the "Google-Profil" findings group
          above, not off on its own near the top — both are about the same
          thing (the Google Business Profile), so they now read together. */}
      {isDetailed && <GbpSelfCheck L={L} />}
    </>
  );
}
